/**
 * Export / import (tech-design §5): a save as a compressed base64 string. This is
 * the player-facing backup, the cross-device transfer format, and — paired with the
 * seeded RNG — the bug-repro format (an exported save replays a run identically).
 *
 * Uses the platform `CompressionStream`/`DecompressionStream` (deflate) and
 * `btoa`/`atob`, all of which exist in both browsers and Node ≥18, so the same code
 * runs in the game and under the unit tests.
 */
import type { SaveEnvelope } from './envelope.js';

/** The structural shape both CompressionStream and DecompressionStream satisfy. */
interface ByteTransform {
  readonly readable: ReadableStream<Uint8Array>;
  readonly writable: WritableStream<BufferSource>;
}

async function runStream(
  bytes: Uint8Array,
  transform: ByteTransform,
): Promise<Uint8Array> {
  // Copy into a guaranteed ArrayBuffer-backed view (TextEncoder output is typed as
  // ArrayBufferLike, which Blob's BufferSource won't accept).
  const input = new Uint8Array(bytes.length);
  input.set(bytes);
  const stream = new Blob([input]).stream().pipeThrough(transform);
  const buffer = await new Response(stream).arrayBuffer();
  return new Uint8Array(buffer);
}

// base64 over raw bytes, chunked so a large save never overflows the arg stack.
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Serialize an envelope to a compressed base64 string suitable for a text field. */
export async function exportSave(envelope: SaveEnvelope): Promise<string> {
  const json = JSON.stringify(envelope);
  const bytes = new TextEncoder().encode(json);
  const compressed = await runStream(bytes, new CompressionStream('deflate'));
  return bytesToBase64(compressed);
}

/**
 * Parse a string produced by {@link exportSave} back into an envelope. Throws on a
 * malformed blob (bad base64, bad deflate stream, or non-JSON) — callers surface a
 * "couldn't read that save" message rather than importing garbage.
 */
export async function importSave(text: string): Promise<SaveEnvelope> {
  const compressed = base64ToBytes(text.trim());
  const bytes = await runStream(compressed, new DecompressionStream('deflate'));
  const json = new TextDecoder().decode(bytes);
  return JSON.parse(json) as SaveEnvelope;
}
